import { Injectable } from '@angular/core';
import { AuthService } from '../auth.service';
import { Observable } from 'rxjs';
import { SchoolFilter, SchoolModel } from '../../models/school/school.model';
import { map } from 'rxjs/operators';
import { StringExtensionModel } from '../../models/extensions/string-extension.model';
import {SchoolLicenseModel} from '../../models/school/school-license.model';
import {UserModel} from '../../models/authentication/user.model';
import {GroupModel} from '../../models/school/group.model';
import {SchoolGroupFilter} from '../school/school-groups.service';
import {SessionActivityModel} from '../../models/student/session.model';
import {AnswerActivityModel} from '../../models/student/answer.model';

@Injectable({
  providedIn: 'root'
})
export class AdminSchoolService {

  constructor(private authSv: AuthService) {
  }

  fetchSchools(filter?: SchoolFilter): Observable<SchoolModel[]> {

    let url = '/admin/schools';

    if (filter) {
      url += StringExtensionModel.queryString(filter);
    }

    return this.authSv.get(url).pipe(map((response) => {
      return SchoolModel.generateList(response.data.schools);

    }));

  }

  fetchSchoolById(schoolId: string): Observable<SchoolModel> {

    return this.authSv.get('/admin/schools/' + schoolId).pipe(map((response) => {

      const school = SchoolModel.generate(response.data);

      return school;

    }));

  }

  updateSchool(school: SchoolModel): Observable<SchoolModel> {

    return this.authSv.patch('/admin/schools', school).pipe(map((response) => {

      const createdSchool = SchoolModel.generate(response.data);

      return createdSchool;

    }));

  }

  fetchActiveLicenses(schoolId: string): Observable<{ license: SchoolLicenseModel, school: SchoolModel }> {

    if (schoolId) {

      const url = `/admin/schools/${schoolId}/activeStudentLicenses`;

      return this.authSv.get(url.toString()).pipe(map((response) => {

        const data = {
          ...response.data.school,
          activeStudents: response.data.activeStudents
        };

        const license = SchoolLicenseModel.generate(data);
        const school = SchoolModel.generate(response.data.school);

        return {
          school,
          license
        };

      }));

    }
  }

  enableLinkInvitation(schoolId: string, value: boolean): Observable<SchoolModel> {

    return this.authSv.patch(`/admin/schools/${schoolId}/enableLinkInvitation`, { linkInvitationEnabled: value }).pipe(map((response) => {

      const school = SchoolModel.generate(response.data);
      return school;

    }));

  }

  generateNewLink(schoolId: string): Observable<SchoolModel> {

    return this.authSv.patch(`/admin/schools/${schoolId}/generateNewLink`, {}).pipe(map((response) => {

      const school = SchoolModel.generate(response.data);
      return school;

    }));

  }

  deleteSchool(schoolId: string, password: string, secret: string): Observable<boolean> {

    return this.authSv.post('/admin/schools/' + schoolId + '/deleteSchool', { password, secret }).pipe(map((response) => {

      return true;

    }));

  }

  fetchGroups(schoolId: string, filter: SchoolGroupFilter = null): Observable<GroupModel[]> {

    let url = `/admin/schools/${schoolId}/groups`;
    url += StringExtensionModel.queryString(filter);

    return this.authSv.get(url).pipe(map((response) => {

      const groups = GroupModel.generateModels(response.data);
      return groups;

    }));

  }

  resolveGroupMigrationContext(schoolId: string, groupId: string): Observable<any> {
    return this.authSv.patch(`/admin/schools/${schoolId}/groups/${groupId}/resolveContext`, {}).pipe(
      map(res => res.data)
    );
  }

  fetchGroupById(schoolId: string, groupId: string): Observable<GroupModel> {

    const url = `/admin/schools/${schoolId}/groups/${groupId}/group`;

    return this.authSv.get(url).pipe(map((response) => {

      const group = GroupModel.generateModel(response.data);
      return group;

    }));

  }

  fetchGroupSessions(schoolId: string, groupId: string, interval: string = null, fromDate: Date = null, toDate: Date = null, sort: number = -1): Observable<SessionActivityModel[]> {

    let url = `/admin/schools/${schoolId}/groups/${groupId}/sessionActivity`;

    const queryObject = {};
    if (fromDate) {
      queryObject['fromDate'] = fromDate;
    }

    if (toDate) {
      queryObject['toDate'] = toDate;
    }

    if (interval) {
      queryObject['interval'] = interval;
    }

    queryObject['sort'] = sort;

    url += StringExtensionModel.queryString(queryObject);

    return this.authSv.get(url).pipe(map((response) => {

      const sessions = SessionActivityModel.generateModels(response.data);
      return sessions;

    }));

  }

  fetchGroupAnswers(schoolId: string, groupId: string, interval: string = null, fromDate: Date = null, toDate: Date = null, groupByStudent = false, sort: number = -1): Observable<AnswerActivityModel[]> {

    let url = `/admin/schools/${schoolId}/groups/${groupId}/answerActivity`;

    const queryObject = {};
    if (fromDate) {
      queryObject['fromDate'] = fromDate;
    }

    if (toDate) {
      queryObject['toDate'] = toDate;
    }

    if (interval) {
      queryObject['interval'] = interval;
    }

    queryObject['groupByStudent'] = groupByStudent;
    queryObject['sort'] = sort;

    url += StringExtensionModel.queryString(queryObject);

    return this.authSv.get(url).pipe(map((response) => {

      const answers = AnswerActivityModel.generateModels(response.data);
      return answers;

    }));

  }

}
